import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Award, DollarSign, Clock, Check, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';

const SPECIALTIES = [
  'Strength', 'Powerlifting', 'Olympic Weightlifting', 'Bodybuilding',
  'CrossFit', 'HYROX', 'Endurance', 'Mobility', 'Rehabilitation',
  'Sports Performance', 'Weight Loss', 'Yoga'
];

const CoachProfileSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    display_name: '',
    bio: '',
    specialties: [],
    certifications: [],
    experience_years: 1,
    hourly_rate: 50,
    availability: 'weekdays'
  });
  const [certInput, setCertInput] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await api.get('/coach/profile/me');
        setExisting(res.data);
        setForm({
          display_name: res.data.display_name || '',
          bio: res.data.bio || '',
          specialties: res.data.specialties || [],
          certifications: res.data.certifications || [],
          experience_years: res.data.experience_years || 1,
          hourly_rate: res.data.hourly_rate || 50,
          availability: res.data.availability || 'weekdays'
        });
      } catch {
        // No profile yet — that's fine
      }
    };
    loadProfile();
  }, []);

  const toggleSpecialty = (s) => {
    setForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(s.toLowerCase())
        ? prev.specialties.filter(x => x !== s.toLowerCase())
        : [...prev.specialties, s.toLowerCase()]
    }));
  };

  const addCert = () => {
    if (!certInput.trim()) return;
    setForm(prev => ({ ...prev, certifications: [...prev.certifications, certInput.trim()] }));
    setCertInput('');
  };

  const removeCert = (idx) => {
    setForm(prev => ({ ...prev, certifications: prev.certifications.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    if (!form.display_name.trim()) { toast.error('Display name is required'); return; }
    if (form.specialties.length === 0) { toast.error('Select at least one specialty'); return; }
    if (!form.bio.trim()) { toast.error('Please add a short bio'); return; }

    setLoading(true);
    try {
      if (existing) {
        await api.put('/coach/profile', form);
        toast.success('Profile updated!');
      } else {
        await api.post('/coach/profile', form);
        toast.success('Profile created! Athletes can now find you.');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-2xl" data-testid="coach-profile-setup">
        <h1 className="text-3xl font-secondary font-bold tracking-tight mb-1">
          {existing ? 'EDIT' : 'SET UP'} YOUR <span className="text-primary">COACH PROFILE</span>
        </h1>
        <p className="text-zinc-400 text-sm mb-8">This is what athletes see when browsing the marketplace.</p>

        <div className="space-y-6">
          {/* Display Name */}
          <div>
            <Label className="hpme-label mb-2 block">DISPLAY NAME</Label>
            <Input className="hpme-input" value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))}
              placeholder="e.g., Coach Sarah" data-testid="profile-name-input" />
          </div>

          {/* Bio */}
          <div>
            <Label className="hpme-label mb-2 block">BIO</Label>
            <Textarea className="hpme-input min-h-[100px]" value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
              placeholder="Tell athletes about your coaching style, experience, and what they can expect..." data-testid="profile-bio-input" />
          </div>

          {/* Specialties */}
          <div>
            <Label className="hpme-label mb-2 block">SPECIALTIES</Label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map(s => (
                <button key={s} type="button" onClick={() => toggleSpecialty(s)}
                  className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                    form.specialties.includes(s.toLowerCase())
                      ? 'bg-primary text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`} data-testid={`specialty-${s.toLowerCase()}`}>
                  {form.specialties.includes(s.toLowerCase()) && <Check className="w-3 h-3 inline mr-1" />}
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Experience & Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="hpme-label mb-2 block">YEARS OF EXPERIENCE</Label>
              <Input type="number" min={0} max={50} className="hpme-input" value={form.experience_years}
                onChange={e => setForm(p => ({ ...p, experience_years: parseInt(e.target.value) || 0 }))} data-testid="profile-experience-input" />
            </div>
            <div>
              <Label className="hpme-label mb-2 block">HOURLY RATE ($)</Label>
              <Input type="number" min={0} className="hpme-input" value={form.hourly_rate}
                onChange={e => setForm(p => ({ ...p, hourly_rate: parseFloat(e.target.value) || 0 }))} data-testid="profile-rate-input" />
            </div>
          </div>

          {/* Certifications */}
          <div>
            <Label className="hpme-label mb-2 block">CERTIFICATIONS</Label>
            <div className="flex gap-2 mb-2">
              <Input className="hpme-input flex-1" value={certInput} onChange={e => setCertInput(e.target.value)}
                placeholder="e.g., CSCS, NASM-CPT" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCert())} data-testid="profile-cert-input" />
              <Button type="button" variant="outline" className="border-zinc-700" onClick={addCert}>Add</Button>
            </div>
            {form.certifications.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.certifications.map((c, i) => (
                  <span key={i} className="px-2 py-1 bg-accent-blue/20 text-accent-blue text-xs rounded-sm flex items-center gap-1">
                    <Award className="w-3 h-3" /> {c}
                    <button onClick={() => removeCert(i)} className="ml-1 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Availability */}
          <div>
            <Label className="hpme-label mb-2 block">AVAILABILITY</Label>
            <div className="flex gap-2">
              {['weekdays', 'weekends', 'evenings', 'flexible'].map(a => (
                <button key={a} type="button" onClick={() => setForm(p => ({ ...p, availability: a }))}
                  className={`px-3 py-1.5 rounded-sm text-sm capitalize transition-colors ${
                    form.availability === a ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`} data-testid={`availability-${a}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button className="hpme-btn-primary w-full py-3" onClick={handleSubmit} disabled={loading} data-testid="save-profile-btn">
            {loading ? 'Saving...' : existing ? 'Update Profile' : 'Create Profile & Go Live'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoachProfileSetup;
